import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    MenuItem,
    CircularProgress,
    Paper,
    Card,
    CardContent,
    Alert,
    Chip,
    Divider,
    Stack,
    Avatar,
    Fade,
    Backdrop,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    CalendarToday,
    Person,
    Home,
    FileDownload,
    Info,
    Assessment,
    DateRange,
    TrendingUp
} from "@mui/icons-material";
import dayjs from "dayjs";
import { ApiDetails } from "../../../../dummyData";

export default function TenantReport({ tenantDetails }) {
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reportType, setReportType] = useState("monthly");
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState("current");

    // Extract tenant data and property_id from tenantDetails
    const tenant = tenantDetails;
    const propertyId = tenant.property_id;

    // Automatically determine which endpoint to use based on date selection
    useEffect(() => {
        if (startDate) {
            const startDateObj = dayjs(startDate);
            const cutoffDate = dayjs("2025-03-01"); // March 2025 cutoff

            if (startDateObj.isBefore(cutoffDate)) {
                setSelectedEndpoint("current");
            } else {
                setSelectedEndpoint("enhanced");
            }
        }
    }, [startDate]);

    const getEndpointInfo = () => {
        if (selectedEndpoint === "current") {
            return {
                color: "warning",
                label: "Legacy System",
                description: "Using original endpoint (pre-March 2025 data)"
            };
        } else {
            return {
                color: "success",
                label: "Enhanced System",
                description: "Using new accurate endpoint (March 2025+ data)"
            };
        }
    };

    const handleGenerateReport = () => {
        if (!startDate || !endDate) {
            showSnackbar("Please select valid dates!", "error");
            return;
        }

        if (reportType === "monthly") {
            generateMonthlyReport();
        } else {
            generateRangeReport();
        }
    };

    const showSnackbar = (text, type) => {
        if (window.snackBar) {
            window.snackBar({ text, type });
        }
    };

    const generateMonthlyReport = () => {
        setIsGenerating(true);
        const startDateObj = dayjs(startDate);

        // Choose endpoint based on date
        const endpoint = selectedEndpoint === "enhanced"
            ? "pegasus/visionary/reports/get/tenant/report/V3"
            : "pegasus/visionary/reports/get/tenant/report";

        let data;

        if (selectedEndpoint === "enhanced") {
            // New enhanced endpoint format
            data = {
                tenant_id: tenant.tenant_id,
                start_date: startDateObj.startOf('month').format('YYYY-MM-DD'),
                end_date: startDateObj.endOf('month').format('YYYY-MM-DD'),
            };
        } else {
            // Original endpoint format
            data = {
                tenant_id: tenant.tenant_id,
                year: startDateObj.year().toString(),
                month: startDateObj.month() + 1,
                first_name: tenant.first_name,
                property_id: propertyId,
                property_name: tenant.property_name || "Property",
            };
        }

        makeApiCall(endpoint, data, "tenant_report.pdf");
    };

    const generateRangeReport = () => {
        setIsGenerating(true);
        const startDateObj = dayjs(startDate);
        const endDateObj = dayjs(endDate);

        if (startDateObj.isAfter(endDateObj) || startDateObj.isSame(endDateObj)) {
            showSnackbar("End date must be after start date", "error");
            return false;
        }

        // Choose endpoint based on date
        const endpoint = selectedEndpoint === "enhanced"
            ? "pegasus/visionary/reports/get/tenant/report/V3"
            : "pegasus/visionary/reports/get/tenant/range/report";

        let data;

        if (selectedEndpoint === "enhanced") {
            // New enhanced endpoint format
            data = {
                tenant_id: tenant.tenant_id,
                start_date: startDateObj.startOf('month').format('YYYY-MM-DD'),
                end_date: endDateObj.endOf('month').format('YYYY-MM-DD')
            };
        } else {
            // Original endpoint format
            data = {
                tenant_id: tenant.tenant_id,
                year: startDateObj.year().toString(),
                month: startDateObj.month() + 1,
                end_year: endDateObj.year().toString(),
                end_month: endDateObj.month() + 1,
                first_name: tenant.first_name,
                property_id: propertyId,
                property_name: tenant.property_name || "Property",
            };
        }

        makeApiCall(endpoint, data, "tenant_range_report.pdf");
    };

    const makeApiCall = (endpoint, data, defaultFileName) => {
        fetch(ApiDetails + endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify(data),
        })
            .then(async (response) => {
                console.log("Response status:", response.status);
                console.log("Response headers:", [...response.headers.entries()]);

                let fileName = defaultFileName;

                // Multiple ways to extract filename
                const contentDisposition = response.headers.get("content-disposition");
                const customFilename = response.headers.get("x-filename");

                console.log("Content-Disposition:", contentDisposition);
                console.log("X-Filename:", customFilename);

                // Try to extract filename from Content-Disposition header
                if (contentDisposition) {
                    // Handle both quoted and unquoted filenames
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
                    if (filenameMatch && filenameMatch[1]) {
                        fileName = filenameMatch[1].replace(/['"]/g, ''); // Remove quotes
                        console.log("Extracted filename from Content-Disposition:", fileName);
                    } else {
                        // Try UTF-8 encoded filename
                        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]*)/i);
                        if (utf8Match && utf8Match[1]) {
                            fileName = decodeURIComponent(utf8Match[1]);
                            console.log("Extracted UTF-8 filename:", fileName);
                        }
                    }
                }

                // Fallback to custom header
                if (customFilename && fileName === defaultFileName) {
                    fileName = customFilename;
                    console.log("Using custom filename header:", fileName);
                }

                if (response.status === 200) {
                    showSnackbar("File Download Will Start In A Few Seconds", "success");

                    // Check content type
                    const contentType = response.headers.get("content-type");
                    console.log("Content-Type:", contentType);

                    // Process and download the file
                    const blob = await response.blob();
                    console.log("Blob size:", blob.size, "bytes");
                    console.log("Blob type:", blob.type);

                    // Validate blob
                    if (blob.size === 0) {
                        showSnackbar("Downloaded file is empty", "error");
                        return;
                    }

                    // Create blob with correct MIME type if needed
                    const correctedBlob = contentType && contentType !== blob.type
                        ? new Blob([blob], { type: contentType })
                        : blob;

                    const href = window.URL.createObjectURL(correctedBlob);

                    // Create download link
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", fileName);
                    link.style.display = "none"; // Hide the link

                    // Add to DOM, click, and remove
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Clean up the object URL
                    setTimeout(() => {
                        URL.revokeObjectURL(href);
                    }, 1000); // Delay cleanup to ensure download starts

                    console.log("Download initiated for:", fileName);

                } else if (response.status === 404) {
                    showSnackbar("No data found for the selected period", "error");
                } else {
                    // Try to read error message from response
                    try {
                        const errorData = await response.json();
                        console.log("Error response:", errorData);
                        const errorMessage = errorData.error || errorData.Message || "Unknown error occurred";
                        showSnackbar(`Error: ${errorMessage}`, "error");
                    } catch (e) {
                        console.log("Could not parse error response:", e);
                        showSnackbar(`Server error (${response.status}). Please try again later.`, "error");
                    }
                }
            })
            .catch((err) => {
                console.error("Fetch error:", err);
                showSnackbar("Network error. Please check your connection and try again.", "error");
            })
            .finally(() => {
                setIsGenerating(false);
            });
    };

    const endpointInfo = getEndpointInfo();

    return (
        <>
            {/* Loading Backdrop */}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isGenerating}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Generating Report...
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                        Please wait while we prepare your financial report
                    </Typography>
                </Box>
            </Backdrop>

            <Paper
                elevation={0}
                sx={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "1px solid #e9ecef"
                }}
            >
                {/* Header Section */}
                <Box sx={{
                    p: 3,
                    bgcolor: "white",
                    borderBottom: "1px solid #e9ecef"
                }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Avatar sx={{ bgcolor: "#6c757d", width: 48, height: 48 }}>
                            <Assessment sx={{ fontSize: 24 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" fontWeight="600" color="text.primary">
                                Financial Report
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Generate comprehensive tenant financial analytics
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Tenant Info Cards */}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                            <Card
                                variant="outlined"
                                sx={{
                                    bgcolor: "#f8f9fa",
                                    border: "1px solid #dee2e6",
                                    boxShadow: "none"
                                }}
                            >
                                <CardContent sx={{ py: 2 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Person sx={{ color: "#6c757d" }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Tenant
                                            </Typography>
                                            <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                                                {tenant.first_name} {tenant.last_name}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card
                                variant="outlined"
                                sx={{
                                    bgcolor: "#f8f9fa",
                                    border: "1px solid #dee2e6",
                                    boxShadow: "none"
                                }}
                            >
                                <CardContent sx={{ py: 2 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Home sx={{ color: "#6c757d" }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Unit
                                            </Typography>
                                            <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                                                {tenant.house_number}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card
                                variant="outlined"
                                sx={{
                                    bgcolor: "#f8f9fa",
                                    border: "1px solid #dee2e6",
                                    boxShadow: "none"
                                }}
                            >
                                <CardContent sx={{ py: 2 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <TrendingUp sx={{ color: "#6c757d" }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Monthly Rent
                                            </Typography>
                                            <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                                                KSH {Number(tenant.rent || 0).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Form Section */}
                <Card sx={{ m: 3, borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "1px solid #e9ecef" }}>
                    <CardContent sx={{ p: 3 }}>
                        {/* System Selection Info */}
                        <Alert
                            severity="info"
                            sx={{
                                mb: 3,
                                borderRadius: "8px",
                                bgcolor: "#e3f2fd",
                                border: "1px solid #bbdefb",
                                color: "#1565c0"
                            }}
                            action={
                                <Chip
                                    label={endpointInfo.label}
                                    color={endpointInfo.color}
                                    size="small"
                                    variant="outlined"
                                />
                            }
                        >
                            <Typography variant="body2" fontWeight="500">
                                {endpointInfo.description}
                            </Typography>
                        </Alert>

                        {/* Report Type Selection */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, display: "flex", alignItems: "center", color: "text.primary" }}>
                                <Assessment sx={{ mr: 1, color: "#6c757d", fontSize: 20 }} />
                                Report Configuration
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={reportType}
                                onChange={(e) => setReportType(e.target.value)}
                                variant="outlined"
                                label="Select Report Type"
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px"
                                    }
                                }}
                            >
                                <MenuItem value="monthly">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CalendarToday fontSize="small" />
                                        <Box>
                                            <Typography variant="body2">Monthly Report</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Generate report for a single month
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </MenuItem>
                                <MenuItem value="duration">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DateRange fontSize="small" />
                                        <Box>
                                            <Typography variant="body2">Duration Report</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Generate report for a date range
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </MenuItem>
                            </TextField>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Date Selection */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, display: "flex", alignItems: "center", color: "text.primary" }}>
                                <DateRange sx={{ mr: 1, color: "#6c757d", fontSize: 20 }} />
                                Date Range Selection
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>
                                            Start Date (Month/Year)
                                        </Typography>
                                        <TextField
                                            type="month"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px"
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>
                                            End Date (Month/Year)
                                        </Typography>
                                        <TextField
                                            type="month"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            disabled={reportType === "monthly"}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px"
                                                }
                                            }}
                                        />
                                        {reportType === "monthly" && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                                End date is automatically set for monthly reports
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Report Preview Info */}
                        {startDate && endDate && (
                            <Fade in={true}>
                                <Alert
                                    severity="success"
                                    sx={{
                                        mb: 3,
                                        borderRadius: "8px",
                                        bgcolor: "#e8f5e8",
                                        border: "1px solid #c8e6c9",
                                        color: "#2e7d32"
                                    }}
                                >
                                    <Typography variant="body2" fontWeight="500">
                                        Report Preview: {dayjs(startDate).format('MMMM YYYY')}
                                        {reportType === "duration" && ` to ${dayjs(endDate).format('MMMM YYYY')}`}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        This report will include billing history, payment records, and outstanding balances.
                                    </Typography>
                                </Alert>
                            </Fade>
                        )}

                        {/* Generate Button */}
                        <Box sx={{ textAlign: "center", mt: 3 }}>
                            <Button
                                variant="contained"
                                size="medium"
                                disabled={isGenerating || !startDate || !endDate}
                                onClick={handleGenerateReport}
                                startIcon={<FileDownload />}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    padding: "10px 32px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    bgcolor: "#495057",
                                    "&:hover": {
                                        bgcolor: "#343a40"
                                    }
                                }}
                            >
                                {isGenerating ? "Generating Report..." : "Generate Financial Report"}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Paper>
        </>
    );
}